import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  getAccountPositions,
  getBrokerageAccount,
  type Position,
  type BrokerageAccount,
  type AccountSummary,
} from "../services/brokerageService";

export function BrokeragePositionsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<BrokerageAccount | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      navigate("/brokerage");
      return;
    }
    loadData();
  }, [accountId, navigate]);

  async function loadData() {
    if (!accountId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [accountData, positionsData] = await Promise.all([
        getBrokerageAccount(accountId),
        getAccountPositions(accountId),
      ]);
      
      setAccount(accountData);
      setPositions(positionsData.positions || []);
      setAccountSummary(positionsData.account || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load positions");
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(value: string | undefined): string {
    if (!value) return "$0.00";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }

  function formatQty(value: string): string {
    const num = parseFloat(value);
    if (Number.isInteger(num)) return num.toString();
    return num.toFixed(4);
  }

  // Calculate totals
  const totalValue = positions.reduce((sum, pos) => sum + parseFloat(pos.market_value || "0"), 0);
  const totalPL = positions.reduce((sum, pos) => sum + parseFloat(pos.unrealized_pl || "0"), 0);
  const totalPLPct = totalValue > 0 ? (totalPL / (totalValue - totalPL)) * 100 : 0;

  return (
    <div>
      <PageHeader
        title={account ? `Positions - ${account.account_name}` : "Positions"}
        subtitle="View your current holdings from Alpaca"
        backgroundImage="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link 
            to="/brokerage" 
            className="text-primary-400 hover:text-primary-300 transition-colors inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Brokerage Accounts
          </Link>
        </div>

        {error && (
          <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading positions...</p>
            </div>
          </div>
        ) : positions.length === 0 ? (
          <div className="glass-card p-6 sm:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-surface-100 mb-2">No Positions</h3>
            <p className="text-surface-400">
              This account doesn't have any open positions.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
              <div className="glass-card p-4">
                <p className="text-sm text-surface-400 mb-1">Total Positions</p>
                <p className="text-2xl font-bold text-surface-100">{positions.length}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-surface-400 mb-1">Positions Value</p>
                <p className="text-2xl font-bold text-surface-100">
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(totalValue)}
                </p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-surface-400 mb-1">Cash</p>
                <p className="text-2xl font-bold text-surface-100">
                  {accountSummary ? formatCurrency(accountSummary.cash) : "$0.00"}
                </p>
              </div>
              <div className="glass-card p-4">
                <p className="text-sm text-surface-400 mb-1">Total Equity</p>
                <p className="text-2xl font-bold text-primary-400">
                  {accountSummary ? formatCurrency(accountSummary.equity) : "$0.00"}
                </p>
              </div>
              <div className="glass-card p-4 col-span-2 sm:col-span-1">
                <p className="text-sm text-surface-400 mb-1">Unrealized P/L</p>
                <p className={`text-2xl font-bold ${totalPL >= 0 ? 'text-accent-400' : 'text-red-400'}`}>
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", signDisplay: "always" }).format(totalPL)}
                  <span className="text-sm ml-2">
                    ({totalPLPct >= 0 ? "+" : ""}{totalPLPct.toFixed(2)}%)
                  </span>
                </p>
              </div>
            </div>

            {/* Positions Table - Desktop */}
            <div className="glass-card overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-surface-700">
                      <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Symbol</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Avg Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Current</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Today</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">P/L</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-700/50">
                    {positions.map((pos) => {
                      const pl = parseFloat(pos.unrealized_pl || "0");
                      const plPct = parseFloat(pos.unrealized_pl_pct || "0") * 100;
                      const changeToday = parseFloat(pos.change_today || "0") * 100;
                      
                      return (
                        <tr key={pos.symbol} className="hover:bg-surface-800/50 transition-colors">
                          <td className="px-4 py-3 font-medium text-surface-100">{pos.symbol}</td>
                          <td className="px-4 py-3 text-right text-surface-300">{formatQty(pos.qty)}</td>
                          <td className="px-4 py-3 text-right text-surface-300">{formatCurrency(pos.avg_entry_price)}</td>
                          <td className="px-4 py-3 text-right text-surface-300">{formatCurrency(pos.current_price)}</td>
                          <td className="px-4 py-3 text-right text-surface-100 font-medium">{formatCurrency(pos.market_value)}</td>
                          <td className={`px-4 py-3 text-right font-medium ${changeToday >= 0 ? 'text-accent-400' : 'text-red-400'}`}>
                            {changeToday >= 0 ? "+" : ""}{changeToday.toFixed(2)}%
                          </td>
                          <td className={`px-4 py-3 text-right font-medium ${pl >= 0 ? 'text-accent-400' : 'text-red-400'}`}>
                            {formatCurrency(pos.unrealized_pl)}
                            <span className="text-xs ml-1">
                              ({plPct >= 0 ? "+" : ""}{plPct.toFixed(2)}%)
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Positions Cards - Mobile */}
            <div className="md:hidden space-y-4">
              {positions.map((pos) => {
                const pl = parseFloat(pos.unrealized_pl || "0");
                const plPct = parseFloat(pos.unrealized_pl_pct || "0") * 100;
                const changeToday = parseFloat(pos.change_today || "0") * 100;
                
                return (
                  <div key={pos.symbol} className="glass-card p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-surface-100">{pos.symbol}</h3>
                        <p className="text-sm text-surface-400">{formatQty(pos.qty)} shares</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-surface-100">{formatCurrency(pos.market_value)}</p>
                        <p className={`text-sm font-medium ${changeToday >= 0 ? 'text-accent-400' : 'text-red-400'}`}>
                          Today: {changeToday >= 0 ? "+" : ""}{changeToday.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-surface-400">Avg Cost</p>
                        <p className="text-surface-200">{formatCurrency(pos.avg_entry_price)}</p>
                      </div>
                      <div>
                        <p className="text-surface-400">Current</p>
                        <p className="text-surface-200">{formatCurrency(pos.current_price)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-surface-400">Unrealized P/L</p>
                        <p className={`font-medium ${pl >= 0 ? 'text-accent-400' : 'text-red-400'}`}>
                          {formatCurrency(pos.unrealized_pl)} ({plPct >= 0 ? "+" : ""}{plPct.toFixed(2)}%)
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
